import {useTranslationWithKeys} from "@/hooks/useTranslationWithKeys.ts";
import {isSupportedLanguage} from "@/utils/localeUtils.ts";
import {logger} from "@/utils/logger.ts";
import {useUpdateProfileMutation} from "@/store/generated/api.ts";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from "@/components/basic/dropdown-menu.tsx";
import {Button} from "@/components/basic/button.tsx";
import {Check, Globe} from "lucide-react";

export function LanguageToggle() {
    const {t, i18n} = useTranslationWithKeys();
    const languages = [
        {code: "en-US", label: t('language.english'), flag: "🇬🇧"},
        {code: "de-DE", label: t('language.german'), flag: "🇩🇪"},
    ];

    const [updateProfile] = useUpdateProfileMutation();

    const handleLanguageChange = async (i18nLocale: string) => {
        const backendLanguageMap: Record<string, string> = {
            'en-US': 'en',
            'de-DE': 'de'
        };

        const backendLanguage = backendLanguageMap[i18nLocale];

        if (!isSupportedLanguage(backendLanguage)) {
            logger.error('Unsupported language:', backendLanguage);
            return;
        }

        i18n.changeLanguage(i18nLocale);

        try {
            await updateProfile({
                vwpAuthUserUpdateRequest: {language: backendLanguage}
            }).unwrap();
        } catch (error) {
            logger.error('Failed to persist language preference:', error);
        }
    };

    const getCurrentLanguage = () => {
        const currentLang = i18n.language || 'en-US';
        return languages.find(l => l.code === currentLang) || languages[0];
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                    <Globe className="h-4 w-4"/>
                    <span>{getCurrentLanguage().flag}</span>
                    <span>{getCurrentLanguage().label}</span>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
                <>
                    {languages.map((lang) => (
                        <DropdownMenuItem
                            key={lang.code}
                            onClick={() => handleLanguageChange(lang.code)}
                            className="flex items-center justify-between cursor-pointer"
                        >
                            <div className="flex items-center gap-2">
                                <span>{lang.flag}</span>
                                <span>{lang.label}</span>
                            </div>
                            <>
                                {getCurrentLanguage().code === lang.code && (
                                    <Check className="h-4 w-4 text-primary"/>
                                )}
                            </>
                        </DropdownMenuItem>
                    ))}
                </>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}